import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';

/**
 * Hook for executing sensitive operations that may require MFA re-verification.
 *
 * Use this hook when performing operations like:
 * - Trusting a new device
 * - Revoking device trust
 * - Changing password
 * - Changing email
 * - Modifying MFA settings
 *
 * The hook checks if MFA verification is required (HTTP 403 with X-MFA-Required header)
 * and prompts the user to re-authenticate if needed.
 *
 * @example
 * ```tsx
 * const { executeSensitiveOperation, isMfaRequired, isExecuting, error } = useSensitiveOperation();
 *
 * const handleTrustDevice = async () => {
 *   await executeSensitiveOperation(async () => {
 *     await api.post('/api/devices/trust', { device_name: 'My Device' });
 *   });
 * };
 * ```
 */

export interface SensitiveOperationState {
  /** Whether MFA verification is currently required */
  isMfaRequired: boolean;
  /** Whether an operation is currently executing */
  isExecuting: boolean;
  /** Error message if operation failed */
  error: string | null;
  /** Execute a sensitive operation with automatic MFA handling */
  executeSensitiveOperation: <T>(operation: () => Promise<T>) => Promise<T | undefined>;
  /** Reset the MFA required state */
  resetMfaRequired: () => void;
  /** Clear any error */
  clearError: () => void;
}

export const useSensitiveOperation = (): SensitiveOperationState => {
  // Note: useAuth() is available if needed for MFA re-verification flow
  useAuth(); // Ensures hook is called consistently for React rules
  const [isMfaRequired, setIsMfaRequired] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetMfaRequired = useCallback(() => {
    setIsMfaRequired(false);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const executeSensitiveOperation = useCallback(
    async <T>(operation: () => Promise<T>): Promise<T | undefined> => {
      setIsExecuting(true);
      setError(null);

      try {
        const result = await operation();
        setIsMfaRequired(false);
        return result;
      } catch (err: unknown) {
        // Check if this is an MFA required error
        const errorObj = err as { response?: { status?: number; headers?: Headers }; message?: string };

        // Check for 403 with MFA-Required header
        if (errorObj.response?.status === 403) {
          // Check for X-MFA-Required header (from backend middleware)
          const mfaRequired = errorObj.response?.headers?.get?.('X-MFA-Required') === 'true';

          if (mfaRequired || errorObj.message?.includes('MFA verification required')) {
            setIsMfaRequired(true);
            setError('Please re-verify with MFA to continue with this action.');
            return undefined;
          }
        }

        // Extract error message
        const errorMessage =
          errorObj.message ||
          (err instanceof Error ? err.message : 'Operation failed');
        setError(errorMessage);
        throw err;
      } finally {
        setIsExecuting(false);
      }
    },
    []
  );

  return {
    isMfaRequired,
    isExecuting,
    error,
    executeSensitiveOperation,
    resetMfaRequired,
    clearError,
  };
};

/**
 * Check if an error indicates MFA re-verification is required.
 *
 * @param error - The error object to check
 * @returns true if MFA verification is required
 */
export const isMfaRequiredError = (error: unknown): boolean => {
  if (!error) return false;

  const errorObj = error as { response?: { status?: number }; message?: string };

  // Check for 403 status
  if (errorObj.response?.status === 403) {
    return true;
  }

  // Check for specific error messages
  if (errorObj.message?.toLowerCase().includes('mfa verification required')) {
    return true;
  }

  return false;
};
